<template>
    <div>
        <div id="app" align-h="center">
            <div v-if="isDevelopmentAPI" class="development-warning text-center bg-info p-3">
                <strong>API</strong>: 
                <b-badge variant="warning">DEVELOPMENT</b-badge> <span>&nbsp;</span> 
                <strong>DB</strong>: 
                <b-badge v-if="dbUsed === 'development'" variant="warning">{{ dbUsed }}</b-badge>
                <b-badge v-else variant="danger">{{ dbUsed }}</b-badge>
            </div>

            <div v-if="!loadingData">
                <router-view
                    :assets="assets"
                    @suggest-buy="openBuy"
                    @asset-add-balance="setBalance"
                    @asset-add-group="addAssetGroup"
                    @manage-group="manageGroup"
                    />
            </div>
            <div v-else class="loading-screen text-center text-secondary p-5 mt-5">
                <b-spinner variant="secondary"></b-spinner><br /><br />
                Loading Assets...
            </div>
            
            <div v-if="loggedIn" class="text-center p-2" :class="syncStatus">
                <span class="sync-data">
                    <b-icon-clock class="text-secondary" /> <span class="mr-3">{{ pricingTime }}</span>
                    <b-icon-arrow-repeat class="text-secondary" /> <span class="text-secondary">{{ syncTook }} / {{ processingTook }}</span>
                </span><br />

                <span class="text-secondary">v{{ appVersion }} {{ getEnv }}</span>

                <div class="pb-5"></div>
                <div class="pb-5"></div>
            </div>
            <app-navbar id="navbar"
                :class="syncStatus" 
                v-if="loggedIn" 
                class="px-2 pt-2 pb-1 mb-3 mx-2"
                />
            
            <!-- PWA Update Prompt -->
            <update-prompt />
        </div>
    </div>
</template>

<script>
import SuggestBuy from './components/overlays/SuggestBuy.vue'
import AddBalance from './components/overlays/AddBalance.vue'
import AddGroup from './components/overlays/AddGroup.vue'
import ManageGroup from './components/overlays/ManageGroup.vue'
import UpdatePrompt from './components/overlays/UpdatePrompt.vue'

import { mapState, mapGetters } from 'vuex';
import AppNavbar from './components/AppNavbar.vue';

import bucketMixin from './mixins/bucketMixin';
import formatter from './mixins/formatter';

import pkg from '../package.json';

import { MODAL_STYLE, SHEET_STYLE } from '@/constants/ui';
import { SW_UPDATE_CHECK_INTERVAL_MS } from '@/constants/sync';
import { SYNC_STATUS } from '@/constants/status';

const appVersion = pkg.version;

export default {
    name: "App",
    mixins: [bucketMixin, formatter],
    components: {
        AppNavbar,
        UpdatePrompt,
    },
    data() {
        return {
            appVersion,
            modalStyle: MODAL_STYLE,
        };
    },
    async created() {
        //get('assets').then((value) => this.$store.dispatch('updateAssets', value));

        this.checkUserSession();

        // bucket list is loaded by bucketMixin's created() hook

        // Check for service worker updates every 5 minutes
        if (process.env.NODE_ENV === 'production') {
            this.$store.dispatch('checkForUpdates');
            setInterval(() => {
                this.$store.dispatch('checkForUpdates');
            }, SW_UPDATE_CHECK_INTERVAL_MS);
        }
    },
    methods: {
        refreshData: function() {
            // called from methods AND from template
            this.$store.dispatch('syncData');
        },
        checkUserSession: async function() {            
            if (this.$store.getters.loggedIn) {
                // active session detected
                this.refreshData();

            } else if (this.$router.currentRoute.name !== 'login') {
                // session expired
                this.$store.dispatch('disableSyncTimer');
                this.$router.push({ name: 'login' });
            } else {   
                // when on login page
                this.$store.dispatch('disableSyncTimer');
            }
        },
        openBuy(symbol = null) {
            this.$FModal.show(
                {
                    component: SuggestBuy,
                    ...SHEET_STYLE
                },
                { symbol }
            )
        },
        setBalance(asset) {
            this.$FModal.show(
                {
                    component: AddBalance,
                    ...SHEET_STYLE
                },
                { assetContext: asset }
            )
        },
        addAssetGroup(groupData) {
            this.$FModal.show(
                {
                    component: AddGroup,
                    ...this.modalStyle
                },
                { 
                    assetContext: groupData.asset,
                    groupType: groupData.defaultGroupType,
                    defaultDrawSize: ((groupData.defaultDrawSize) ? groupData.defaultDrawSize : null),
                    defaultMultiplier: ((groupData.defaultMultiplier) ? groupData.defaultMultiplier : null),
                    defaultOpenPrice: ((groupData.defaultOpenPrice) ? groupData.defaultOpenPrice : null),
                    defaultGroupName: ((groupData.defaultGroupName) ? groupData.defaultGroupName : null),
                }
            )
        },
        manageGroup(groupData) {
            this.$FModal.show(
                {
                    component: ManageGroup,
                    ...this.modalStyle
                },
                { 
                    assetContext: groupData.asset,
                    groupContext: groupData.group,
                }
            )
        }
    },
    computed: {
        ...mapState({
            assets: state => state.assets.assetList,
        }),
        ...mapGetters([
            'hasAssetsToSell',
            'hasAssetsToBuy',
            'getEnv',
        ]),
        isDevelopmentAPI() {
            return ((this.getEnv === 'development') ? true : false);
        },
        dbUsed() {
            return this.$store.state.dbUsed;
        },
        loggedIn() {
            return this.$store.getters.loggedIn;
        },
        syncTook() {
            return this.$store.state.syncTook
        },
        processingTook() {
            return this.$store.state.processingTook
        },
        pricingTime() {
            const pricingTime = this.$store.state.pricingTime;
            if (!pricingTime) return 'N/A';

            return this.formatTimeFromISO(pricingTime).split(' ').join('');
        },
        syncStatus() {
            let status = this.$store.state.syncStatus;

            if (status === SYNC_STATUS.SYNCED) {
                // selling wins the colour even when there are also buys
                if (this.hasAssetsToSell) return 'status_assets_to_sell';
                if (this.hasAssetsToBuy) return 'status_assets_to_buy';
            }

            return 'status_'+status;
        },
        loadingData() {
            if (!this.loggedIn) return false

            if (this.loggedIn && this.assets && this.assets.length) return false

            return true
        }
    },
    watch: {
        loggedIn(newState, prevState) {
            if (newState !== prevState) { // hack ca sa folosesc var names din fn declaration
                this.checkUserSession();
                // logging in after an expired session has to reload the buckets
                if (newState) this.ensureBucketList();
            }
        },
        '$store.state.requestWarnings'(newWarnings) {
            if (!newWarnings || !newWarnings.length) return;

            for (const message of newWarnings) {
                this.$bvToast.toast(message, {
                    title: 'Warning',
                    autoHideDelay: 5000,
                    toaster: 'b-toaster-top-center',
                    variant: 'warning',
                    solid: true,
                });
            }
        }
    }
    
};
</script>

<style lang="scss">

@import 'styles/_custom.scss';
@import url("https://fonts.googleapis.com/css2?family=Karla:wght@300&display=swap");

#app {
    font-family: "Karla", sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    color: #fff;
    margin-bottom: 100px;
    padding: 0px;
    max-width: 600px;
    margin: 0 auto;
}

#app .formatPrice { 
    font-family: var(--font-family-monospace);
    font-weight: normal;
}

#app .development-warning {
    text-transform: uppercase;
}

#navbar { 
    position: fixed;
    bottom: 0px;
    z-index: 999;
    width: calc(100% - 1rem);
    max-width: 600px;
    border-bottom: 3px solid #e83e8c;
}
#navbar.status_not_synced {
    border-color: #e83e8c;
}
#navbar.status_syncing {
    border-color: #9659f8;
}
.status_syncing .sync-data {
    color: #9659f8;
} 
#navbar.status_processing {
    border-color: #ffc107;
}
.status_processing .sync-data {
    color: #ffc107;
}
#navbar.status_getActionable {
    border-color: #ff8b07;
}
#navbar.status_synced {
    border-color: #343a40;
}
#navbar.status_assets_to_sell {
    border-color: #B2FFD6;
}
#navbar.status_assets_to_buy {
    border-color: #e83e8c
}

.modal-style { 
        color: #fff; 
        padding: 30px 15px 50px 15px; 
    }

.custom-table-head {
    background-color: hsla(0,0%,100%,.05);
}
.oddRow {
    background-color: hsla(0,0%,100%,.05);
}
.format-price {
    font-family: var(--font-family-monospace);
    font-size: 0.9em;
    line-height: 1.8em;
}
.strikethrough {
    text-decoration: line-through;
}
</style>
